/**
 * CoachMarks — interactive first-use tour that spotlights REAL UI elements.
 *
 * Unlike a static guide, this dims the app and points at the actual button the
 * doctor should tap, one step at a time, with plain-language explanations.
 *
 * - Steps anchor to elements via [data-tour="..."] attributes
 * - Spotlight cutout follows the element (works with fixed/scrolling layouts)
 * - "Next" / "Skip tour" — fully dismissible, never traps the user
 * - Re-runnable from Settings → "Replay app tour"
 */
import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'

const TOUR_DONE_KEY = 'scribey_tour_done'

export function isTourDone(): boolean {
  try { return !!localStorage.getItem(TOUR_DONE_KEY) } catch { return true }
}
export function markTourDone() {
  try { localStorage.setItem(TOUR_DONE_KEY, '1') } catch {}
}
export function resetTour() {
  try { localStorage.removeItem(TOUR_DONE_KEY) } catch {}
}

interface TourStep {
  target: string // data-tour value
  title: string
  body: string
}

const STEPS: TourStep[] = [
  {
    target: 'capture',
    title: 'This is the only button you really need',
    body: 'Tap Capture when you\u2019re with a patient (or ready to dictate). Just talk \u2014 say the patient\u2019s name and what kind of visit it is, and Dr. Scribey fills everything in for you. When you\u2019re done talking, your note is written.',
  },
  {
    target: 'schedule-import',
    title: 'Start your day with one photo',
    body: 'Have a printed schedule? Snap a photo of it here and your whole day appears as a patient list \u2014 then each visit is just one tap. (Totally optional \u2014 Capture always works on its own.)',
  },
  {
    target: 'new-visit',
    title: 'Prefer typing or planning ahead?',
    body: 'New Visit lets you pick the patient and note type by hand before you start. Use it when you want more control \u2014 otherwise just use Capture.',
  },
  {
    target: 'nav-visits',
    title: 'Your notes all live here',
    body: 'Every visit you capture shows up on this list with its status \u2014 Draft means it\u2019s waiting for your review, Approved means done. Tap any visit to read or fix its note.',
  },
  {
    target: 'assistant',
    title: 'Stuck? Just ask.',
    body: 'This is your AI assistant. Ask it anything in plain English \u2014 \u201chow do I change my note format?\u201d, \u201cwhen did I last see Mrs. Smith?\u201d \u2014 and it will help or do it for you.',
  },
]

function getVisibleTarget(name: string): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`)
  for (const el of nodes) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0 && el.offsetParent !== null) return el
    // fixed-position elements have offsetParent === null but are visible
    if (r.width > 0 && r.height > 0 && getComputedStyle(el).position === 'fixed') return el
  }
  return null
}

export function CoachMarks({ onDone }: { onDone: () => void }) {
  const [stepIdx, setStepIdx] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [tick, setTick] = useState(0)

  // Resolve current step → skip steps whose target isn't on screen
  const resolved = useMemo(() => {
    for (let i = stepIdx; i < STEPS.length; i++) {
      const el = getVisibleTarget(STEPS[i].target)
      if (el) return { idx: i, el }
    }
    return null
  }, [stepIdx, tick])

  useEffect(() => {
    if (!resolved) { finish(); return }
    const el = resolved.el
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    const update = () => setRect(el.getBoundingClientRect())
    update()
    const t = setTimeout(update, 350) // after smooth scroll
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved?.idx, resolved?.el])

  function finish() {
    markTourDone()
    onDone()
  }

  function next() {
    if (!resolved || resolved.idx >= STEPS.length - 1) { finish(); return }
    setStepIdx(resolved.idx + 1)
    setTick((t) => t + 1)
  }

  if (!resolved || !rect) return null
  const step = STEPS[resolved.idx]
  const stepNumber = resolved.idx + 1

  // Tooltip placement: below target if there's room, else above
  const pad = 8
  const spotTop = rect.top - pad
  const spotLeft = rect.left - pad
  const spotW = rect.width + pad * 2
  const spotH = rect.height + pad * 2
  const placeBelow = rect.bottom + 190 < window.innerHeight
  const tipStyle: React.CSSProperties = placeBelow
    ? { top: rect.bottom + 16 }
    : { bottom: window.innerHeight - rect.top + 16 }

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-label="App tour">
      {/* Spotlight: a hole with a huge shadow dims everything else */}
      <div
        className="absolute rounded-xl transition-all duration-300 pointer-events-none"
        style={{
          top: spotTop,
          left: spotLeft,
          width: spotW,
          height: spotH,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
          border: '2px solid rgba(168, 130, 255, 0.9)',
        }}
      />
      {/* Click shield (blocks interaction during tour, but X/skip always works) */}
      <div className="absolute inset-0" onClick={next} />

      {/* Tooltip card */}
      <div
        className="absolute left-4 right-4 mx-auto max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-5 transition-all duration-300"
        style={tipStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-bold text-gray-900 dark:text-gray-100 text-base leading-snug">
            {step.title}
          </h3>
          <button
            onClick={finish}
            aria-label="Close tour"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 -mt-1 -mr-1 p-1"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${
                  i === resolved.idx ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={finish}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            >
              Skip tour
            </button>
            <button
              onClick={next}
              className="bg-brand-600 hover:bg-brand-500 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors"
            >
              {stepNumber === STEPS.length ? 'Got it!' : `Next (${stepNumber}/${STEPS.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
